import i18n from 'i18next'
import { enqueueSnackbar } from 'notistack'
import { coalesceRequest } from '../lib/client/coalesceRequest'
import { reportError } from '../lib/client/error'
import { errorSnackbarOptions } from '../lib/client/snackbar'
import { isObject, parseJSON } from '../lib/utils'
import { appVersion } from '../lib/version'
import { API_BASE_URL } from '../routeConfig'

/**
 * An error body is whatever the endpoint returned. The named fields are the ones shared code looks for;
 * the index signature admits an endpoint's own payload, which a caller narrows before reading.
 */
type Body =
  | ({ message?: string; cancelled?: boolean; email?: string; error?: string; reason?: string } & Record<
      string,
      unknown
    >)
  | string

interface HttpResponse<T> {
  data: T
  status: number
}

interface HttpFullResponse<T> extends HttpResponse<T> {
  headers: Headers
}

interface HttpRequestInit extends RequestInit {
  coalesceRevision?: number | string
  timeoutMs?: number
}

const NETWORK_RETRY_DELAY_MS = 400

const getDefaultHttpTimeoutMs = () => {
  const configuredHttpTimeoutMs = Number(process.env.REACT_APP_HTTP_TIMEOUT_MS)
  return Number.isFinite(configuredHttpTimeoutMs) && configuredHttpTimeoutMs > 0 ? configuredHttpTimeoutMs : 30_000
}

const getCoalesceKey = (path: string, init: HttpRequestInit | undefined): string | undefined => {
  if (init?.signal || init?.body !== undefined || (init?.method && init.method.toUpperCase() !== 'GET'))
    return undefined

  const { body: _body, headers, method: _method, signal: _signal, ...options } = init ?? {}
  const optionEntries = Object.entries(options)
  if (
    optionEntries.some(([, value]) => {
      const type = typeof value
      return value !== null && type !== 'boolean' && type !== 'number' && type !== 'string' && type !== 'undefined'
    })
  ) {
    return undefined
  }

  const normalizedHeaders: Array<[string, string]> = []
  new Headers(headers).forEach((value, key) => {
    normalizedHeaders.push([key, value])
  })
  normalizedHeaders.sort(([left], [right]) => left.localeCompare(right))
  optionEntries.sort(([left], [right]) => left.localeCompare(right))

  return `http:get:${JSON.stringify({ headers: normalizedHeaders, options: optionEntries, path })}`
}

const getStatusFromBody = (body: Body | undefined, fallback: string = ''): string => {
  if (isObject(body)) return body.message ?? fallback

  return body ?? fallback
}

const setAuthorizationHeader = (headers: HeadersInit | undefined, token: string): HeadersInit => {
  const authorization = `Bearer ${token}`

  if (headers instanceof Headers) {
    const next = new Headers(headers)
    next.set('Authorization', authorization)
    return next
  }

  if (Array.isArray(headers)) {
    return [...headers.filter(([key]) => key.toLowerCase() !== 'authorization'), ['Authorization', authorization]]
  }

  return { ...headers, Authorization: authorization }
}

export class APIError extends Error {
  status: number
  statusText: string
  body?: Body
  /** The failed response's headers, for an endpoint that says more in them than in its body. */
  headers: Headers

  constructor(response: Response, body: Body) {
    const jsonStatus = getStatusFromBody(body)
    const statusText = response.statusText || jsonStatus
    const message = `${response.status} ${statusText}`
    super(message)
    this.status = response.status
    this.statusText = statusText
    this.body = body
    this.headers = response.headers
  }
}

/**
 * The code a refusal carries, where it carries one. A 4xx that the handler threw with a body says
 * what it refused in a field of its own; anything else says nothing and reads the same as an unknown
 * refusal here: a network failure and a bare string have no code, and the `error` of an unhandled 500
 * is the wrapper's constant, which matches none.
 */
export const refusalCode = (error: unknown): string | undefined => {
  if (!(error instanceof APIError) || !isObject(error.body)) return undefined

  const body: Record<string, unknown> = error.body
  return typeof body.error === 'string' ? body.error : undefined
}

function anySignal(signals: AbortSignal[]): AbortSignal {
  const controller = new AbortController()

  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort(signal.reason)
      break
    }

    signal.addEventListener('abort', () => controller.abort(signal.reason), {
      signal: controller.signal,
    })
  }

  return controller.signal
}

const isNetworkError = (error: unknown): boolean => {
  if (!(error instanceof Error) || error.name === 'AbortError') return false

  return /connection|fetch|internet|load failed|network/i.test(error.message)
}

const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'

const shouldRetryRequest = (init: HttpRequestInit, error: unknown): boolean =>
  init.method?.toUpperCase() === 'GET' && !init.signal?.aborted && !(error instanceof APIError) && isNetworkError(error)

const reportRequestError = (error: unknown) => {
  if (isAbortError(error)) return

  if (!(error instanceof APIError)) {
    enqueueSnackbar(isNetworkError(error) ? i18n.t('error.connectionInterrupted') : `${error}`, errorSnackbarOptions)
  }
  reportError(error)
}

const retryRequest = async <T>(path: string, init: HttpRequestInit): Promise<HttpFullResponse<T> | undefined> => {
  await new Promise((resolve) => setTimeout(resolve, NETWORK_RETRY_DELAY_MS))
  if (init.signal?.aborted) return undefined
  return httpWithTimeout<T>(path, init)
}

async function httpWithTimeout<T>(path: string, init: HttpRequestInit): Promise<HttpFullResponse<T>> {
  const url = API_BASE_URL + path
  const { coalesceRevision: _coalesceRevision, timeoutMs = getDefaultHttpTimeoutMs(), ...requestInit } = init

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort('timeout'), timeoutMs)
  const signal = requestInit.signal ? anySignal([controller.signal, requestInit.signal]) : controller.signal

  try {
    const response = await fetch(url, {
      ...requestInit,
      headers: {
        ...requestInit.headers,
        Accept: 'application/json',
        // Logged by API Gateway access logging. A request with no version came from a bundle
        // older than this header, which is the only way to tell those clients apart.
        'X-Client-Version': appVersion,
      },
      signal,
    })

    clearTimeout(timer)

    const text = await response.text()
    if (!response.ok) {
      let json = text
      try {
        json = parseJSON(text)
      } catch (e) {
        reportError(e)
      }
      throw new APIError(response, json)
    }
    // The caller names the wire shape it expects; the typed boundary (`lib/wire.ts`) builds the model from it.
    const parsed = parseJSON(text) as T
    return { data: parsed, headers: response.headers, status: response.status }
  } catch (err) {
    clearTimeout(timer)

    if (controller.signal.aborted && controller.signal.reason === 'timeout') {
      throw new APIError(new Response(null, { status: 408, statusText: `timeout loading ${url}` }), {})
    }

    throw err
  }
}

async function http<T>(path: string, init: HttpRequestInit): Promise<HttpFullResponse<T>> {
  try {
    const result = await httpWithTimeout<T>(path, init)

    return result
  } catch (initialError) {
    let err = initialError

    // A network switch commonly rejects an otherwise safe read request. Retry it once;
    // mutations are deliberately left to their callers because their outcome may be unknown.
    if (shouldRetryRequest(init, err)) {
      try {
        const result = await retryRequest<T>(path, init)
        if (result !== undefined) return result
      } catch (retryError) {
        err = retryError
      }
    }

    reportRequestError(err)
    throw err
  }
}

const dataOf = async <T>(request: Promise<HttpFullResponse<T>>): Promise<T> => (await request).data

const statusOf = async <T>(request: Promise<HttpFullResponse<T>>): Promise<HttpResponse<T>> => {
  const { data, status } = await request
  return { data, status }
}

const getResponse = <T>(path: string, init: HttpRequestInit | undefined) => {
  const request = () => http<T>(path, { method: 'GET', ...init })
  const coalesceKey = getCoalesceKey(path, init)
  return coalesceKey ? coalesceRequest(coalesceKey, request) : request()
}

const HTTP = {
  async delete<T, U>(path: string, body: T, init?: HttpRequestInit): Promise<U> {
    return dataOf(http<U>(path, { body: JSON.stringify(body), method: 'DELETE', ...init }))
  },
  async get<T>(path: string, init?: HttpRequestInit): Promise<T> {
    return dataOf(getResponse<T>(path, init))
  },
  /** A GET whose caller also needs the response headers, which `get` leaves out. */
  async getWithHeaders<T>(path: string, init?: HttpRequestInit): Promise<{ data: T; headers: Headers }> {
    const { data, headers } = await getResponse<T>(path, init)
    return { data, headers }
  },
  async patch<T, U>(path: string, body: T, init?: HttpRequestInit): Promise<HttpResponse<U>> {
    return statusOf(http<U>(path, { body: JSON.stringify(body), method: 'PATCH', ...init }))
  },
  async post<T, U>(path: string, body: T, init?: HttpRequestInit): Promise<HttpResponse<U>> {
    return statusOf(http<U>(path, { body: JSON.stringify(body), method: 'POST', ...init }))
  },
  async postRaw<T extends BodyInit, U>(path: string, body: T, init?: HttpRequestInit): Promise<U> {
    return dataOf(http<U>(path, { body, method: 'POST', ...init }))
  },
  async put<T, U>(path: string, body: T, init?: HttpRequestInit): Promise<U> {
    return dataOf(http<U>(path, { body: JSON.stringify(body), method: 'PUT', ...init }))
  },
}

export const withToken = (init: HttpRequestInit, token?: string): HttpRequestInit => ({
  ...init,
  headers: token ? setAuthorizationHeader(init.headers, token) : init.headers,
})

const setHeader = (headers: HeadersInit | undefined, name: string, value: string): HeadersInit => {
  if (headers instanceof Headers) {
    const next = new Headers(headers)
    next.set(name, value)
    return next
  }
  if (Array.isArray(headers)) {
    return [...headers.filter(([key]) => key.toLowerCase() !== name.toLowerCase()), [name, value]]
  }
  return { ...headers, [name]: value }
}

/**
 * The participant's credentials for a registration call. Logged in, the id token is the bearer
 * token for the authorizer and the registration's edit token travels in its own header; otherwise
 * the edit token is the bearer token as before (KOE-1418).
 */
export const withRegistrationAuth = (init: HttpRequestInit, editToken?: string, idToken?: string): HttpRequestInit => {
  if (!idToken) return withToken(init, editToken)

  const headers = setAuthorizationHeader(init.headers, idToken)
  return { ...init, headers: editToken ? setHeader(headers, 'X-Registration-Token', editToken) : headers }
}

/**
 * Calls the logged-in variant of a route when there is an id token, and the public one otherwise.
 * A 401 from the logged-in route (an expired token) falls back to the public route, so a token that
 * ran out never stands between the participant and their registration.
 */
export const withUserRouteFallback = async <T>(
  idToken: string | undefined,
  request: (idToken?: string) => Promise<T>
): Promise<T> => {
  if (!idToken) return request()
  try {
    return await request(idToken)
  } catch (error) {
    if (error instanceof APIError && error.status === 401) return request()
    throw error
  }
}

export default HTTP
